import { useEffect, useState } from 'react';
import { PipelineItem } from '../../const/PipelineTypes';
import api from '../../api/axiosInstance';
import { useLoader } from '../../hooks/useLoader';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';

export const useFetchPipeline = (
  initialPipeline: PipelineItem | null,
  ticketId: string | undefined,
  automationFlowId: string | undefined,
) => {
  const [pipeline, setPipeline] = useState<PipelineItem | null>(initialPipeline);
  const [isPipelineLoading, setIsPipelineLoading] = useState(!initialPipeline);
  const [pipelineError, setPipelineError] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();

  useEffect(() => {
    const fetchPipeline = async () => {
      if (!pipeline && ticketId && !pipelineError) {
        try {
          showLoader();
          setIsPipelineLoading(true);
          setPipelineError(false);
          const response = await api.get(`/api/workflows/${ticketId}/${automationFlowId}`);
          setPipeline(response.data);
        } catch (err: unknown) {
          console.error('Failed to load pipeline data:', err);
          setPipelineError(true);
          addNotifications([{
            title: 'Error',
            icon: 'icon-triangle-warning',
            description: err instanceof Error ? err.message : 'Failed to load pipeline data',
            timestamp: new Date(),
          }]);
        } finally {
          setTimeout(() => {
            hideLoader();
            setIsPipelineLoading(false);
          }, 500);
        }
      }
    };
    fetchPipeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, pipeline, pipelineError]);

  // On-demand refresh of the pipeline (e.g. after submit, so gnbDeploySubmitted flips and the
  // UI transitions to the node status view without a manual page reload).
  const refetch = async () => {
    if (!ticketId) return;
    try {
      const response = await api.get(`/api/workflows/${ticketId}/${automationFlowId}`);
      setPipeline(response.data);
      setPipelineError(false);
    } catch (err: unknown) {
      console.error('Failed to refresh pipeline data:', err);
    }
  };

  return { pipeline, isPipelineLoading, refetch };
};
